import {Modal, ModalBody, ModalHeader, ModalFooter, Button} from "reactstrap";
import {useContext, useState} from 'react';
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    Form,
    FormGroup,
    Label,
} from "reactstrap";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {toast} from "react-toastify";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import AuthContext from "../../context/AuthContext";
import {changePaymentStatus, createInvoiceForOrder, editOrderInfo, getInvoiceByOrder, triggerRazorpayOrder} from "../../api/api";
import Avatar from "react-avatar";
import Loader from "../../components/Loader";
import IssueCreditNoteModal from "./IssueCreditNoteModal";
import IssueRefundModal from "./IssueRefundModal";
import {useNavigate} from "react-router-dom";

const ORDER_STATUS_OPTIONS = [
    { value: 'BEING-PROCESSED', label: 'Being Processed' },
    { value: 'IN-TRANSIT',      label: 'In Transit' },
    { value: 'COMPLETE',        label: 'Complete' },
    { value: 'CANCELLED',       label: 'Cancelled' },
];

const ALLOWED_NEXT_STATUSES = {
    'PENDING':         ['BEING-PROCESSED', 'IN-TRANSIT', 'COMPLETE', 'CANCELLED'],
    'BEING-PROCESSED': ['IN-TRANSIT', 'COMPLETE', 'CANCELLED'],
    'IN-TRANSIT':      ['COMPLETE', 'CANCELLED'],
};

function ActionButton(props) {
    const {refreshToken} = useContext(AuthContext);
    const navigate = useNavigate();
    const [actionButtonDropdownOpen, setActionButtonDropdownOpen] = useState(false);
    const [orderStatusModalOpen, setOrderStatusModalOpen] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [setStatus, setSetStatus] = useState(props.order.order_status);
    const [paymentMethod, setPaymentMethod] = useState(props.order.payment_method);
    // const [qrCodeImage, setQrCodeImage] = useState(props.order.qr_code_image);
    const [qrCodeURL, setQrCodeURL] = useState(props.order.qr_code_url);
    const [orderItemsModalOpen, setOrderItemsModalOpen] = useState(false);
    const [cancellationReason, setCancellationReason] = useState('');

    const [orderStatusConfirmationModal, setOrderStatusConfirmationModal] = useState(false);
    const [orderPendingStatus, setOrderPendingStatus] = useState(null);
    const [creditNoteModalOpen, setCreditNoteModalOpen] = useState(false);
    const [invoiceId, setInvoiceId] = useState(null);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [loadingCreditNoteNavigation, setLoadingCreditNoteNavigation] = useState(false);
    const [issueRefundModalOpen, setIssueRefundModalOpen] = useState(false);

    const handleorderState = (status) => {
        if (status === "COMPLETE" || status === "CANCELLED") {
            setOrderPendingStatus(status);
            setOrderStatusConfirmationModal(true);
        } else {
            setSetStatus(status);
        }
    };

    const handleOrderConfirmChange = () => {
        const formData = new FormData();
        formData.append("order_status", orderPendingStatus);
        if (orderPendingStatus === 'CANCELLED' && cancellationReason) {
            formData.append("order_cancellation_reason", cancellationReason);
        }
        changeOrderStatusMutation.mutate({ formdata: formData, orderId: props.order.id });
        setOrderStatusConfirmationModal(false);
        setOrderStatusModalOpen(false);
    };

    const handleOrderCancelChange = () => {
        setOrderPendingStatus(null);
        setOrderStatusConfirmationModal(false);
    };

    const handleCancelChange = () => {
        if (orderPendingStatus === 'CANCELLED') {
            props.order.order_cancellation_reason = cancellationReason;
        }
        handleOrderCancelChange();
    };
    const toggleActionButtonDropdown = () => {
        setActionButtonDropdownOpen(prevState => {
            const newDropdownState = !prevState;
        document.querySelector('.table-responsive').classList.toggle('dropdown-open', newDropdownState);
            return newDropdownState;
        });
    }
    const toggleOrderStatusModal = () => {
        if (!orderStatusModalOpen) {
            const allowed = ALLOWED_NEXT_STATUSES[props.order.order_status] || [];
            if (allowed.length > 0) setSetStatus(allowed[0]);
        }
        setOrderStatusModalOpen((prevState) => !prevState);
    };
    const togglePaymentModal = () => setPaymentModalOpen((prevState) => !prevState);
    const toggleOrderItemsModal = () => setOrderItemsModalOpen((prevState) => !prevState);
    const toggleCreditNoteModal = () => setCreditNoteModalOpen((prevState) => !prevState);
    const toggleIssueRefundModal = () => setIssueRefundModalOpen((prevState) => !prevState);

    const handleIssueCreditNote = async () => {
        setLoadingInvoice(true);
        try {
            const invoiceData = await getInvoiceByOrder(props.order.id);
            if (invoiceData?.results?.length > 0) {
                setInvoiceId(invoiceData.results[0].id);
                setCreditNoteModalOpen(true);
            } else {
                toast.error("No paid invoice found for this order.");
            }
        } catch (err) {
            toast.error("Failed to fetch invoice for this order.");
        } finally {
            setLoadingInvoice(false);
        }
    };

    const queryClient = useQueryClient();

    const changeOrderStatusMutation = useMutation({
        mutationFn: editOrderInfo,
        onSuccess: (data) => {
            if (data.qr_code_url) {
                setQrCodeURL(data.qr_code_url);
            }
            queryClient.invalidateQueries(['orders']);
            toast.success('Order Status Changed!');
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, changeOrderStatusMutation, variables);
            if (err.response.status !== 401) {
                toast.error(`An error occurred! ${err?.response?.data?.detail}`);
            }
        }
    });

    const changePaymentStatusMutation = useMutation({
        mutationFn: changePaymentStatus,
        onSuccess: (data, variables) =>{
            queryClient.invalidateQueries(['orders'])
            toast.success('Payment Updated!')
        },
        // onError: error => {
        //     toast.error(`An error occurred! ${err?.response?.data?.detail}`)
        // },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, changePaymentStatusMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })

    const triggerRazorpayMutation = useMutation({
        mutationFn: triggerRazorpayOrder,
        onSuccess: (data) => {
            toast.success('Razorpay order created successfully!');
            queryClient.setQueriesData({ queryKey: ['orders'] }, (old) => {
                if (!old?.results) return old;
                return {
                    ...old,
                    results: old.results.map((o) =>
                        o.id === props.order.id
                            ? { ...o, reference_order_id: data.reference_order_id, payment_method: null }
                            : o
                    ),
                };
            });
        },
        onError: (err) => {
            handleUnauthorized401MutateError(err, refreshToken, triggerRazorpayMutation, props.order.id);
            if (err.response?.status !== 401) {
                const detail = err.response?.data?.detail || 'Failed to create Razorpay order.';
                toast.error(detail);
            }
        },
    });

    const handleTriggerRazorpay = () => {
        triggerRazorpayMutation.mutate(props.order.id);
    };

    const createInvoiceMutation = useMutation({
        mutationFn: createInvoiceForOrder,
        onSuccess: (data) => {
            toast.success(data?.created ? 'Invoice created successfully!' : 'Invoice already exists.');
            queryClient.invalidateQueries(['orders']);
            queryClient.invalidateQueries(['invoices']);
        },
        onError: (err) => {
            handleUnauthorized401MutateError(err, refreshToken, createInvoiceMutation, props.order.id);
            if (err.response?.status !== 401) {
                const detail = err.response?.data?.detail || 'Failed to create invoice.';
                toast.error(detail);
            }
        },
    });

    const handleCreateInvoice = () => {
        createInvoiceMutation.mutate(props.order.id);
    };

    const handleChangeOrderStatus = async () => {
        const formData = new FormData();
        formData.append("order_status", setStatus);
        if (setStatus === 'CANCELLED' && cancellationReason) {
            formData.append("order_cancellation_reason", cancellationReason);
        }
        changeOrderStatusMutation.mutate({
            formdata: formData,
            orderId: props.order.id
        });
        toggleOrderStatusModal();
        toast.success('Order Status Changed!');
    };

    const handleChangePaymentStatusToMarkAsPaid = async (order) => {
        let paymentStatus = {
            payment_status: "PAID"
        };
        changePaymentStatusMutation.mutate({
            paymentStatus: paymentStatus, orderId: order.id
        })
    }

    const handleSavePayment = () => {
        const formData = new FormData();
        formData.append("payment_method", paymentMethod);
        changeOrderStatusMutation.mutate({
            formdata: formData,
            orderId: props.order.id
        });
        toast.success(`Payment method set to ${paymentMethod}`);
        togglePaymentModal();
    };

    const handleGenerateQrCode = () => {
        const formData = new FormData();
        formData.append("payment_method", "UPI");
        changeOrderStatusMutation.mutate({
            formdata: formData,
            orderId: props.order.id
        });
    };

    const handleViewInvoice = () => {
        navigate('/sell/invoices', {
            state: {
                invoiceOrder: props.order.id
            }
        });
    };

    const handleViewCreditNotes = async () => {
        setLoadingCreditNoteNavigation(true);
        try {
            const invoiceData = await getInvoiceByOrder(props.order.id);
            if (invoiceData?.results?.length > 0) {
                navigate('/sell/credit-notes', {
                    state: {
                        cnInvoice: invoiceData.results[0].id
                    }
                });
            } else {
                toast.error("No invoice found for this order.");
            }
        } catch (err) {
            toast.error("Failed to fetch invoice for this order.");
        } finally {
            setLoadingCreditNoteNavigation(false);
        }
    };

    const handleViewTransactions = () => {
        navigate('/sell/transactions', {
            state: {
                order: props.order.id
            }
        });
    };

    const handleViewRefunds = () => {
        navigate('/sell/refunds', {
            state: {
                refundOrder: props.order.id
            }
        });
    };

    return (
        <td className="text-center">
            <Dropdown
                nav
                isOpen={actionButtonDropdownOpen}
                toggle={toggleActionButtonDropdown}
                className="btn-group dropdown"
            >
                <DropdownToggle
                    caret
                    className="dropdown-toggle arrow-none btn btn-light btn-sm"
                >
                    <i className="mdi mdi-dots-horizontal"/>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-right profile-dropdown">
                    {!(setStatus === "COMPLETE" || setStatus === "CANCELLED") && (
                    <DropdownItem
                        className="dropdown-item"
                        onClick={toggleOrderStatusModal}
                    >
                        <i className="fas fa-parachute-box" />
                        <span>
                            Order Status
                        </span>
                    </DropdownItem>
                    )}
                    {!(props.order.payment_status === "PAID" || props.order.payment_status === "REFUNDED" || setStatus === "CANCELLED") && (
                    <DropdownItem
                        className="dropdown-item"
                        // onClick={togglePaymentModal}
                        onClick={() => handleChangePaymentStatusToMarkAsPaid(props.order)}
                    >
                        <i className="fas fa-cash-register" />
                        <span>
                            Mark as Paid by Cash
                        </span>
                    </DropdownItem>
                    )}
                    {!props.order.reference_order_id &&
                        props.order.payment_status === 'UNPAID' &&
                        props.order.order_status !== 'CANCELLED' && (
                        <DropdownItem
                            className="dropdown-item"
                            onClick={handleTriggerRazorpay}
                            disabled={triggerRazorpayMutation.isLoading}
                        >
                            <i className="fas fa-bolt" />
                            <span>
                                {triggerRazorpayMutation.isLoading ? 'Initiating...' : 'Collect via Razorpay'}
                            </span>
                        </DropdownItem>
                    )}
                    <DropdownItem onClick={toggleOrderItemsModal}>
                        <i className="fas fa-boxes" />
                        <span>Order Items</span>
                    </DropdownItem>
                    <DropdownItem onClick={() => navigate(`/sell/orders/${props.order.id}/short-bill`)}>
                        <i className="fas fa-receipt" />
                        <span>Print Short Bill</span>
                    </DropdownItem>
                    {!props.order.has_invoice && props.order.order_status !== "CANCELLED" && (
                        <DropdownItem
                            className="dropdown-item"
                            onClick={handleCreateInvoice}
                            disabled={createInvoiceMutation.isLoading}
                        >
                            <i className="fas fa-file-invoice" />
                            <span>{createInvoiceMutation.isLoading ? "Creating..." : "Create Invoice"}</span>
                        </DropdownItem>
                    )}
                    {props.order.has_invoice && (
                        <DropdownItem
                            className="dropdown-item"
                            onClick={handleViewInvoice}
                        >
                            <i className="fas fa-file-invoice" />
                            <span>View Invoice</span>
                        </DropdownItem>
                    )}
                    <DropdownItem
                        className="dropdown-item"
                        onClick={handleViewCreditNotes}
                        disabled={loadingCreditNoteNavigation}
                    >
                        <i className="fas fa-file-alt" />
                        <span>{loadingCreditNoteNavigation ? "Loading..." : "View Credit Notes"}</span>
                    </DropdownItem>
                    <DropdownItem
                        className="dropdown-item"
                        onClick={handleViewTransactions}
                    >
                        <i className="fas fa-exchange-alt" />
                        <span>View Transactions</span>
                    </DropdownItem>
                    <DropdownItem
                        className="dropdown-item"
                        onClick={handleViewRefunds}
                    >
                        <i className="fas fa-undo-alt" />
                        <span>View Refunds</span>
                    </DropdownItem>
                    {props.order.payment_status === "PAID" && props.order.order_status !== "CANCELLED" && (
                        <DropdownItem
                            className="dropdown-item"
                            onClick={handleIssueCreditNote}
                            disabled={loadingInvoice}
                        >
                            <i className="fas fa-file-invoice" />
                            <span>
                                {loadingInvoice ? "Loading..." : "Issue Credit Note"}
                            </span>
                        </DropdownItem>
                    )}
                    {props.order.payment_status === "PAID" && (
                        <DropdownItem
                            className="dropdown-item"
                            onClick={toggleIssueRefundModal}
                        >
                            <i className="fas fa-undo-alt" />
                            <span>Issue Refund</span>
                        </DropdownItem>
                    )}
                </DropdownMenu>
            </Dropdown>

            <Modal isOpen={orderStatusModalOpen} toggle={toggleOrderStatusModal}>
                <ModalHeader toggle={toggleOrderStatusModal} close={
                    <button className="close" onClick={toggleOrderStatusModal}>
                        &times;
                    </button>
                }>
                    Change Order Status
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="orderStatusSelect">Order Status</Label>
                            <Input
                                type="select"
                                id="orderStatusSelect"
                                className="form-control"
                                value={setStatus}
                                onChange={(e) => handleorderState(e.target.value)}
                            >
                                {ORDER_STATUS_OPTIONS
                                    .filter(opt => (ALLOWED_NEXT_STATUSES[props.order.order_status] || []).includes(opt.value))
                                    .map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))
                                }
                            </Input>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleChangeOrderStatus} disabled={changeOrderStatusMutation.isLoading}>
                    {changeOrderStatusMutation.isLoading? <><Loader size={'small'}/>Saving...</>: `Save`}
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={orderStatusConfirmationModal} toggle={() => setOrderStatusConfirmationModal(false)}>
                <ModalHeader toggle={() => setOrderStatusConfirmationModal(false)} close={
                    <button className="close" onClick={() => setOrderStatusConfirmationModal(false)}>
                        &times;
                    </button>
                }>
                    Confirm Status Change
                </ModalHeader>
                <ModalBody>
                    Are you sure you want to change the status to {orderPendingStatus}?
                    Once the Order Status is changed to {orderPendingStatus}, it cannot be reversed.
                    {orderPendingStatus === 'CANCELLED' && (
                        <FormGroup>
                            <Label for="cancellationReason">Cancellation Reason<span className="text-danger">*</span></Label>
                            <Input
                                type="textarea"
                                id="cancellationReason"
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                                placeholder="Please provide the reason for cancellation"
                            />
                        </FormGroup>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onClick={handleOrderConfirmChange}
                        disabled={changeOrderStatusMutation.isLoading ||
                            (orderPendingStatus === 'CANCELLED' && !cancellationReason.trim())}>
                    {changeOrderStatusMutation.isLoading? <><Loader size={'small'}/>Confirming...</>: `Confirm`}
                    </Button>
                    <Button color="secondary" onClick={handleCancelChange}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={paymentModalOpen} toggle={togglePaymentModal}>
                <ModalHeader toggle={togglePaymentModal} close={
                    <button className="close" onClick={togglePaymentModal}>
                        &times;
                    </button>
                }>
                    Select Payment Method
                </ModalHeader>
                <ModalBody>
                    <div className="order-buttons">
                        <Button
                            color={paymentMethod === "CASH" ? "primary" : "secondary"}
                            onClick={() => setPaymentMethod("CASH")}
                            disabled={changeOrderStatusMutation.isLoading}
                        >
                           {changeOrderStatusMutation.isLoading? <><Loader size={'small'}/>Cash</>: `Cash`} 
                        </Button>
                        <Button
                            color={paymentMethod === "CARD" ? "primary" : "secondary"}
                            onClick={() => setPaymentMethod("CARD")}
                            disabled={changeOrderStatusMutation.isLoading}
                        >
                            {changeOrderStatusMutation.isLoading? <><Loader size={'small'}/>Card</>: `Card`}
                        </Button>
                        <Button
                            color={paymentMethod === "UPI" ? "primary" : "secondary"}
                            onClick={() => {
                                setPaymentMethod("UPI");
                            }}
                            disabled={changeOrderStatusMutation.isLoading}
                        >
                            {changeOrderStatusMutation.isLoading? <><Loader size={'small'}/>UPI</>: `UPI`}
                        </Button>
                    </div>

                    {paymentMethod === "CASH" && (
                        <div className="mt-3">
                            <Label htmlFor="cashReceived">Cash To Collect</Label>
                            <Input
                                type="number"
                                id="cashAmount"
                                className="form-control"
                                value={props.order.total_amount}
                                readOnly
                            />
                        </div>
                    )}

                    {paymentMethod === "CARD" && (
                        <div className="mt-3">
                            <label htmlFor="cardAmount">Card Amount</label>
                            <Input
                                type="number"
                                id="cardAmount"
                                className="form-control"
                                value={props.order.total_amount}
                                readOnly
                            />
                        </div>
                    )}

                    {paymentMethod === "UPI" && (
                        <div className="mt-3 text-center">
                            {!qrCodeURL && !changeOrderStatusMutation.isLoading && (
                                <Button color="primary" onClick={handleGenerateQrCode}>Generate QR Code</Button>
                            )}
                            {changeOrderStatusMutation.isLoading && (
                                <Button color="primary" disabled type="button">
                                    <span role="status" className="spinner-border text-white spinner-border-sm me-1"></span>
                                    Loading...
                                </Button>
                            )}
                            {qrCodeURL && (
                                <img
                                    src={qrCodeURL}
                                    alt="QR Code"
                                    className="img-fluid mt-3"
                                    style={{ maxWidth: "200px" }}
                                />
                            )}
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="primary" onClick={handleSavePayment} disabled={changeOrderStatusMutation.isLoading}>
                    {changeOrderStatusMutation.isLoading? <><Loader size={'small'}/>Saving...</>: `Save`}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal className="modal-full text-center" isOpen={orderItemsModalOpen} toggle={toggleOrderItemsModal}>
                <ModalHeader toggle={toggleOrderItemsModal} close={
                    <button className="close" onClick={toggleOrderItemsModal}>
                        &times;
                    </button>
                }>Order Items</ModalHeader>
                <ModalBody>
                    <table className="table mb-0" style={{ marginTop: '-17px' }}>
                        <thead>
                        <tr>
                            <th>#</th>
                            <th>Image</th>
                            <th>Item Name</th>
                            <th>Item Type</th>
                            <th>Rate</th>
                            <th>Quantity</th>
                            <th>Price Before Tax(Before Discount)</th>
                            <th>Discount</th>
                            <th>Price Before Tax(After Discount)</th>
                            <th>Tax Percentage</th>
                            <th>Tax Amount</th>
                            <th>Price After Tax</th>
                        </tr>
                        </thead>
                        <tbody>
                        {props.order.order_items.map((item, index) => (
                            <tr key={item.id}>
                                <th scope="row">{index + 1}</th>
                                <td>
                                    {item?.product_add_on_item?.product_image || item?.product_group_add_on_item?.product_group_image ? (
                                        <img
                                            src={
                                                item?.product_add_on_item?.product_image ||
                                                item?.product_group_add_on_item?.product_group_image
                                            }
                                            className="avatar-sm img-thumbnail rounded-circle"
                                            alt="product"
                                        />
                                    ) : (
                                        <Avatar
                                            size={35}
                                            name={
                                                item?.name
                                            }
                                            round
                                        />
                                    )}
                                </td>
                                <td style={{ height: '40px' }}>
                                    {item.name}
                                    {item.product_add_on_item?.add_on_products && item.product_add_on_item.add_on_products.map((addOn, addOnIndex) => (
                                        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>
                                            <div className="addon-item" style={{ marginLeft: '5px' }}>
                                                {addOn.name}
                                            </div>
                                        </div>
                                    ))}
                                    {item.product_group_add_on_item?.add_on_products && item.product_group_add_on_item.add_on_products.map((addOn, addOnIndex) => (
                                        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>
                                            <div className="addon-item" style={{ marginLeft: '5px' }}>
                                                {addOn.name}
                                            </div>
                                        </div>
                                    ))}
                                    {/*{item.coupon && (*/}
                                    {/*    <span>*/}
                                    {/*        Applied Coupon: {item.coupon}*/}
                                    {/*    </span>*/}
                                    {/*)}*/}
                                </td>
                                <td>{item.type}</td>
                                <td>₹ {item.rate}</td>
                                <td>{item?.quantity}</td>
                                <td>{item?.amount_total_before_tax_before_discount}</td>
                                <td>
                                    {item?.coupon_discount}

                                    {item?.coupon && (
                                        <>
                                            <br />
                                            <span>Applied Coupon: {item?.coupon}</span>
                                        </>
                                    )}
                                </td>
                                <td>{item.amount_total_before_tax_after_discount}</td>
                                <td>{item.tax_percentage} %</td>
                                <td>{item.tax_amount}</td>
                                <td>₹ {item.total_amount}</td>

                                {/*<td>*/}
                                {/*    {item?.tax_percentage?.toFixed(2)} %*/}
                                {/*    <span className="text-muted ml-1">*/}
                                {/*        ({item.product_add_on_item?.tax_type === 'TAX-INCLUSIVE' || item.product_group_add_on_item?.tax_type === 'TAX-INCLUSIVE' ? 'incl' : 'excl'})*/}
                                {/*    </span>*/}
                                {/*    {item.product_add_on_item?.add_on_products && item.product_add_on_item.add_on_products.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                {addOn.tax_percentage} %*/}
                                {/*                <span className="text-muted ml-1">*/}
                                {/*                    ({addOn.tax_type === 'TAX-INCLUSIVE' ? 'incl' : 'excl'})*/}
                                {/*                </span>*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*    {item.product_group_add_on_item?.add_on_products && item.product_group_add_on_item.add_on_products.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                {addOn.tax_percentage} %*/}
                                {/*                <span className="text-muted ml-1">*/}
                                {/*                    ({addOn.tax_type === 'TAX-INCLUSIVE' ? 'incl' : 'excl'})*/}
                                {/*                </span>*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*</td>*/}
                                {/*<td>*/}
                                {/*    ₹ {item?.tax_amount?.toFixed(2)}*/}
                                {/*    {item.product_add_on_item?.add_on_products && item?.product_add_on_item?.add_on_products.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.tax_amount}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*    {item.product_group_add_on_item?.add_on_products && item.product_group_add_on_item.add_on_products.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.tax_amount}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*</td>*/}
                                {/*<td>*/}
                                {/*    ₹ {item.product_add_on_item?.price_after_tax ||*/}
                                {/*    item.product_group_add_on_item?.price_after_tax}*/}
                                {/*    {item.product_add_on_item?.add_on_products && item.product_add_on_item.add_on_products.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.price_after_tax}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*    {item.product_group_add_on_item?.add_on_products && item.product_group_add_on_item.add_on_products.map((addOn, addOnIndex) => (*/}
                                {/*        <div key={addOnIndex} style={{ marginTop: '5px', fontSize: '12px', color: '#888' }}>*/}
                                {/*            <div className="addon-item" style={{ marginLeft: '5px' }}>*/}
                                {/*                ₹ {addOn.price_after_tax}*/}
                                {/*            </div>*/}
                                {/*        </div>*/}
                                {/*    ))}*/}
                                {/*</td>*/}
                                {/*<td>₹ {(item?.coupon_discount ?? 0).toFixed(2)}</td>*/}
                                {/*<td>{item.quantity}</td>*/}
                                {/*<td className="total-width">₹ {item?.amount_total_after_tax.toFixed(2)}</td>*/}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {/*<div>*/}
                    {/*    <p style={{ height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', margin: 0, padding: '10px' }}>*/}
                    {/*        Cart Items Total:  ₹ {props.order.total_amount}*/}
                    {/*    </p>*/}
                    {/*</div>*/}
                </ModalBody>
                <ModalFooter className="d-flex justify-content-end">
                    <div style={{ textAlign: 'right', lineHeight: '1.8' }}>
                        <p className="mb-0">
                            <strong>Sub Total:</strong> ₹ {props?.order?.sub_total_before_discount || 0.00} <br />
                            {props?.order?.coupon_discount > 0 && (
                                <>
                                    <strong>Discount:</strong> ₹ {props?.order?.coupon_discount} <br />
                                    <strong>Sub Total (After Discount):</strong> ₹ {props?.order?.sub_total_after_discount} <br />
                                </>
                            )}
                            <strong>Effective Tax Percentage:</strong> {props?.order?.effective_tax_percentage || 0.00} % <br />
                            {props?.order?.cgst > 0 && (
                                <>
                                    <strong>CGST:</strong> ₹ {props?.order?.cgst} <br />
                                </>
                            )}
                            {props?.order?.sgst > 0 && (
                                <>
                                    <strong>SGST:</strong> ₹ {props?.order?.sgst} <br />
                                </>
                            )}
                            {props?.order?.utgst > 0 && (
                                <>
                                    <strong>UTGST:</strong> ₹ {props?.order?.utgst} <br />
                                </>
                            )}
                            {props?.order?.igst > 0 && (
                                <>
                                    <strong>IGST:</strong> ₹ {props?.order?.igst} <br />
                                </>
                            )}
                            <strong>Total Tax Amount:</strong> ₹ {props?.order?.total_tax_amount || 0.00} <br />
                            <strong>Order Total:</strong> ₹ {props?.order?.total_amount || 0.00}
                        </p>
                    </div>
                </ModalFooter>
            </Modal>

            {invoiceId && (
                <IssueCreditNoteModal
                    isOpen={creditNoteModalOpen}
                    toggle={toggleCreditNoteModal}
                    order={props.order}
                    invoiceId={invoiceId}
                />
            )}
            <IssueRefundModal
                isOpen={issueRefundModalOpen}
                toggle={toggleIssueRefundModal}
                order={props.order}
            />
        </td>
    );
}

export default ActionButton;
